/**
 * Auth-specific retry utilities
 * Provides retry logic for authentication operations with smart error handling
 */

import {
  withRetry,
  isRetryableError,
  isPersonnelError,
  isTokenError,
  normalizeError,
} from './errorHandler';

/**
 * Custom retry logic for auth operations
 * Don't retry personnel errors or token errors as they won't succeed on retry
 */
const shouldRetryAuthOperation = (error) => {
  const normalized = normalizeError(error);

  // Never retry personnel errors - they indicate the user doesn't exist
  if (normalized.isPersonnelError || isPersonnelError(error)) {
    return false;
  }

  // Never retry token errors - they indicate session/token issues that won't be fixed by retry
  if (normalized.isTokenError || isTokenError(error)) {
    return false;
  }

  // Only retry if it's a retryable error (network, timeout, server error)
  return isRetryableError(error);
};

/**
 * Retry wrapper for token fetching operations
 * Uses fewer retries and shorter delays since token operations should be fast
 */
export const withTokenRetry = async (tokenFn) => {
  return withRetry(tokenFn, {
    maxRetries: 2,
    delayMs: 500,
    shouldRetry: shouldRetryAuthOperation,
    context: 'Token Operation',
  });
};

/**
 * Retry wrapper for email verification checks
 * Uses moderate retry settings
 */
export const withEmailVerificationRetry = async (emailVerificationFn) => {
  return withRetry(emailVerificationFn, {
    maxRetries: 2,
    delayMs: 1000,
    shouldRetry: shouldRetryAuthOperation,
    context: 'Email Verification',
  });
};

/**
 * Retry wrapper for sign-in operations
 * Uses standard retry settings for backend operations
 */
export const withSignInRetry = async (signInFn) => {
  return withRetry(signInFn, {
    maxRetries: 3,
    delayMs: 1000,
    shouldRetry: shouldRetryAuthOperation,
    context: 'Sign In',
  });
};

/**
 * Retry wrapper for whoami/user info fetches
 * Uses standard retry settings
 */
export const withUserInfoRetry = async (userInfoFn) => {
  return withRetry(userInfoFn, {
    maxRetries: 3,
    delayMs: 1000,
    shouldRetry: shouldRetryAuthOperation,
    context: 'User Info Fetch',
  });
};

/**
 * Retry wrapper for verification email resend
 * Uses fewer retries to avoid spamming
 */
export const withResendVerificationRetry = async (resendFn) => {
  return withRetry(resendFn, {
    maxRetries: 2,
    delayMs: 2000,
    shouldRetry: shouldRetryAuthOperation,
    context: 'Resend Verification Email',
  });
};

const authRetryUtils = {
  withTokenRetry,
  withEmailVerificationRetry,
  withSignInRetry,
  withUserInfoRetry,
  withResendVerificationRetry,
  shouldRetryAuthOperation,
};

export default authRetryUtils;
